import { fieldUpdater } from '@shared/Frontend/utils/updaters'
import { Map } from 'immutable'
import {
  any,
  async,
  AsyncState,
  HttpResult,
  IOWidget,
  loadingAsyncState,
  none,
  onlyIf,
  Option,
  some,
  unloadedAsyncState,
  Updater,
  wait
} from 'widgets-for-react'

interface Redirect {
  From: string
  To: string
  StatusCode: number
  Source: string
}

const serverRedirectApi = {
  fetchRedirectForPath: async (path: string): Promise<HttpResult<Option<Redirect>>> => {
    const headers = new Headers()
    headers.append('X-XSRF-TOKEN', (document.getElementsByName('__RequestVerificationToken')[0] as any).value)
    const response = await fetch('/api/v1/redirect/from?path=' + path, { credentials: 'include', headers })

    if (response.status === 404 || response.status === 204) {
      return { kind: 'result', status: response.status, value: none() }
    }

    const body = await response.text()

    if (!response.ok) return { kind: 'failed', status: response.status }

    try {
      const payload = JSON.parse(body)
      return { kind: 'result', status: response.status, value: some(payload) }
    } catch (e) {
      console.error('Error parsing redirect response', e)
      return { kind: 'failed', status: 599 }
    }
  }
}

export interface ServerRedirectState {
  path: string // The current path (after potential redirect)
  cache: Map<string, string> // The store of requested redirects so far
  requestedPath: string // The path that triggered the redirect asyncstate
  redirect: AsyncState<Option<Redirect>> // The async state that fetches redirects from the server
}

const updaters = {
  path: fieldUpdater<ServerRedirectState>()('path'),
  requestedPath: fieldUpdater<ServerRedirectState>()('requestedPath'),
  redirect: fieldUpdater<ServerRedirectState>()('redirect'),
  cache: fieldUpdater<ServerRedirectState>()('cache'),
  invoke: {
    loadRedirect:
      (path: string): Updater<ServerRedirectState> =>
      (state) => ({
        ...state,
        requestedPath: path,
        redirect: loadingAsyncState(() => serverRedirectApi.fetchRedirectForPath(path))
      }),
    followRedirect: (s0: ServerRedirectState): ServerRedirectState => {
      const from = window.location
      const to = new URL(s0.cache.get(from.pathname + from.search + from.hash) ?? '', from.toString())
      if (from.toString() != to.toString()) {
        // Use the window to navigate if the destination belongs to an external origin
        if (to.origin != from.origin) {
          window.location.replace(to.toString())
          return
        }
        // Replace so that the user only needs to perform 1 "back" action to go to the previous page
        window.history.replaceState(null, '', to.pathname + to.search + to.hash)
        window.history.go(0)
      }
      return updaters.path((_: string) => to.pathname + to.search + to.hash)(s0)
    }
  },
  handle: {
    redirectLoaded:
      (u: Updater<ServerRedirectState>): Updater<ServerRedirectState> =>
      (s0) => {
        var s1 = u(s0)
        if (s1.redirect.kind == 'loaded' && s0.redirect.kind == 'loading') {
          const { From, To } =
            s1.redirect.value.kind == 'l' ? { From: s1.requestedPath, To: s1.requestedPath } : s1.redirect.value.v
          return {
            ...s1,
            cache: s1.cache.set(From, To),
            redirect: unloadedAsyncState()
          }
        }
        return s1
      }
  }
}

/**
 * Initializes the serverRedirect widget with the current location preset. It assumes that the initialization is done on a non-redirect route, and sets it as a non-redirect (i.e. from and to are the same).
 * @returns The initial state for the serverRedirect widget
 */
export const initialServerRedirectState = (): ServerRedirectState => {
  const path = location.pathname + location.search + location.hash
  return {
    path,
    requestedPath: path,
    cache: Map<string, string>().set(path, path),
    redirect: unloadedAsyncState()
  }
}

/**
 * The serverRedirect widget will follow redirects from the server on soft navigation.
 * @param state The ServerRedirectState
 * @returns A widget that follows redirects on soft navigation
 */
export const serverRedirectWidget: IOWidget<ServerRedirectState, Updater<ServerRedirectState>> = (state) =>
  any([
    // Only perform redirects from the cache
    onlyIf(
      state.path != location.pathname + location.search + location.hash &&
        state.cache.has(location.pathname + location.search + location.hash),
      wait(0)<Updater<ServerRedirectState>>(() => updaters.invoke.followRedirect)
    ),
    // Request a redirect from the server if the path is not present in the cache
    onlyIf(
      !state.cache.has(location.pathname + location.search + location.hash) && !(state.redirect.kind == 'loading') && !location.pathname.includes('/search'),
      wait(0)(() => updaters.invoke.loadRedirect(location.pathname + location.search + location.hash))
    ),
    async(state.redirect, undefined, { retry: { maxAttempts: 0 }, key: 'redirect' })
      .map(updaters.redirect)
      .map(updaters.handle.redirectLoaded)
  ])
